import React from 'react';
import { formatCurrency } from '../utils/helpers';

interface NetWorthDataPoint {
  date: string;
  value: number;
}

interface NetWorthChartProps {
  data: NetWorthDataPoint[];
  period: 'hour' | 'day' | 'week' | 'month';
}

export const NetWorthChart: React.FC<NetWorthChartProps> = ({ data, period }) => {
  if (data.length === 0) return null;

  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    switch (period) {
      case 'hour':
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      case 'day':
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      case 'week':
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      case 'month':
        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      default:
        return date.toLocaleDateString();
    }
  };

  // Make chart wide and not very tall
  const width = 600;
  const height = 180;
  const padding = 32;
  const chartWidth = width - (padding * 2);
  const chartHeight = height - (padding * 2);

  const getPath = () => {
    if (data.length < 2) return '';
    const points = data.map((point, index) => {
      const x = padding + (index / (data.length - 1)) * chartWidth;
      const y = padding + chartHeight - ((point.value - minValue) / range) * chartHeight;
      return `${x},${y}`;
    });
    return `M ${points.join(' L ')}`;
  };

  const isPositive = data[data.length - 1]?.value > data[0]?.value;
  const change = data[data.length - 1]?.value - data[0]?.value;
  const changePercent = data[0]?.value ? (change / data[0].value) * 100 : 0;

  return (
    <div className="bg-white dark:bg-gray-800 border border-neutral-200 dark:border-neutral-700 rounded-xl p-4 shadow-sm flex flex-col items-center w-full max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-2 w-full">
        <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200">Net Worth Trend</h3>
        <div className="text-right">
          <div className={`text-xs font-medium ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
            {isPositive ? '+' : ''}{formatCurrency(change)}
          </div>
          <div className={`text-[10px] ${isPositive ? 'text-emerald-500 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'}`}>
            {isPositive ? '+' : ''}{changePercent.toFixed(1)}%
          </div>
        </div>
      </div>

      <div className="relative w-full flex justify-center">
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-auto max-w-full rounded-2xl"
          style={{ aspectRatio: `${width} / ${height}` }}
        >
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width="60" height="36" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 36" fill="none" stroke="#f3f4f6" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width={width} height={height} fill="url(#grid)" rx="24" />

          {/* Gradient for the line */}
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={isPositive ? '#10b981' : '#ef4444'} stopOpacity="0.15"/>
              <stop offset="100%" stopColor={isPositive ? '#10b981' : '#ef4444'} stopOpacity="0.03"/>
            </linearGradient>
          </defs>

          {/* Area fill */}
          <path
            d={`${getPath()} L ${width},${height} L 0,${height} Z`}
            fill="url(#lineGradient)"
            opacity="1"
          />

          {/* Line */}
          <path
            d={getPath()}
            stroke={isPositive ? '#10b981' : '#3b82f6'}
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points (optional, can be commented out for a cleaner look) */}
          {/* {data.map((point, index) => {
            const x = padding + (index / (data.length - 1)) * chartWidth;
            const y = padding + chartHeight - ((point.value - minValue) / range) * chartHeight;
            return (
              <circle
                key={index}
                cx={x}
                cy={y}
                r="3"
                fill={isPositive ? '#10b981' : '#3b82f6'}
                className="transition-all duration-200"
              />
            );
          })} */}
        </svg>

        {/* X-axis labels */}
        <div className="flex justify-between text-xs text-neutral-500 dark:text-neutral-400 mt-2 w-full px-2 absolute left-0 right-0 bottom-0">
          {data.length > 0 && (
            <>
              <span>{formatDate(data[0].date)}</span>
              <span>{formatDate(data[Math.floor(data.length / 2)].date)}</span>
              <span>{formatDate(data[data.length - 1].date)}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}; 